import { supabase } from "@/integrations/supabase/client";

// Data transformation utilities
const PERCENTAGE_METRICS = [
  'grad_rate',
  'retention_rate',
  'pell_grad_rate',
  'non_pell_grad_rate',
  'grads_earning_more',
  'percent_full_time_faculty_2',
  'percent_full_time_faculty_3'
];

const RANK_METRICS = [
  'bibliometric_rank',
  'faculty_resources_rank',
  'faculty_salaries_rank',
  'financial_resources_rank',
  'national_rank',
  'outcomes_rank',
  'social_mobility_rank'
];

interface MetricTransformation {
  fromDB: (value: number) => number;
  toDB: (value: number) => number;
}

const transformations: Record<string, MetricTransformation> = {
  default: {
    fromDB: (value: number) => value,
    toDB: (value: number) => value
  },
  percentage: {
    // No transformation needed as DB stores actual percentages
    fromDB: (value: number) => value,
    toDB: (value: number) => value
  },
  rank: {
    fromDB: (value: number) => value,
    toDB: (value: number) => Math.round(value) // Ensure rank values are integers
  }
};

const getTransformation = (metricName: string): MetricTransformation => {
  if (RANK_METRICS.includes(metricName)) {
    return transformations.rank;
  }
  return PERCENTAGE_METRICS.includes(metricName) 
    ? transformations.percentage 
    : transformations.default;
};

const transformMetric = (metricName: string, value: number, direction: 'fromDB' | 'toDB'): number => {
  const transformation = getTransformation(metricName);
  return transformation[direction](value);
};

const transformUniversityData = (data: any, direction: 'fromDB' | 'toDB'): any => {
  if (!data) return null;
  
  const transformed = { ...data };
  for (const [key, value] of Object.entries(data)) {
    if (value !== null && typeof value === 'number') {
      transformed[key] = transformMetric(key, value, direction);
    }
  }
  
  return transformed;
};

interface UpdateMetricsParams {
  university: string;
  metricName: string;
  value: number;
}

export const fetchUniversityRankings = async () => {
  try {
    if (!universitiesCache) {
      const { data: allData, error: dataError } = await supabase
        .from('US News University Rankings')
        .select('*')
        .order('overall_score', { ascending: false })
        .order('university');

      if (dataError) {
        console.error("Error fetching university rankings:", dataError);
        throw dataError;
      }

      const transformedData = (allData || []).map(uni => transformUniversityData(uni, 'fromDB'));
      universitiesCache = transformedData;
    }
    
    return universitiesCache;
  } catch (error) {
    console.error("Error in fetchUniversityRankings:", error);
    throw error;
  }
};

export const fetchDePaulData = async () => {
  try {
    console.log("Fetching DePaul data...");
    const { data, error } = await supabase
      .from('US News University Rankings')
      .select('*')
      .eq('university', 'DePaul University')
      .maybeSingle();
      
    if (error) {
      console.error("Error fetching DePaul data:", error);
      throw error;
    }
    
    if (!data) {
      console.error("No data found for DePaul University");
      return null;
    }

    const transformedData = transformUniversityData(data, 'fromDB');
    console.log("DePaul data fetched and transformed:", transformedData);
    return transformedData;
  } catch (error) {
    console.error("Error in fetchDePaulData:", error);
    throw error;
  }
};

export const updateMetrics = async ({ university, metricName, value }: UpdateMetricsParams) => {
  try {
    console.log("Updating metrics:", { university, metricName, value });
    universitiesCache = null; // Clear cache to force fresh data fetch

    // Transform the value before sending to DB
    const dbValue = transformMetric(metricName, value, 'toDB');

    const { data, error } = await supabase.rpc(
      'update_university_metrics',
      {
        university_name: university,
        metric_name: metricName,
        new_value: dbValue
      }
    );

    if (error) {
      console.error("Error updating metrics:", error);
      throw error;
    }

    console.log("Metrics updated successfully:", data);

    const updatedData = await fetchDePaulData();
    if (!updatedData) {
      throw new Error("Failed to fetch updated data");
    }

    return {
      score: updatedData.overall_score,
      rank: updatedData.national_rank,
      ...data
    };
  } catch (error) {
    console.error("Error in updateMetrics:", error);
    throw error;
  }
};

// Cache for non-DePaul universities' data
let universitiesCache: any[] | null = null;

export const calculateNewRank = async (newScore: number) => {
  try {
    console.log('Calculating new rank for score:', newScore);
    
    // Always fetch fresh data for rank calculation
    console.log('Fetching universities from database...');
    const { data: allData, error: dataError } = await supabase
      .from('US News University Rankings')
      .select('*')
      .order('overall_score', { ascending: false });

    if (dataError) throw dataError;

    // Remove DePaul from the comparison set and sort by score
    const sortedUniversities = allData
      .filter(uni => uni.university !== 'DePaul University')
      .sort((a, b) => b.overall_score - a.overall_score);

    console.log('Top 5 universities by score:');
    sortedUniversities.slice(0, 5).forEach(uni => {
      console.log(`${uni.university}: Score ${uni.overall_score}, Rank ${uni.national_rank}`);
    });

    // Find universities with scores around our target
    const nearbyScores = sortedUniversities.filter(uni => 
      Math.abs(uni.overall_score - newScore) <= 0.5
    );

    console.log('\nUniversities with similar scores (±0.5):');
    nearbyScores.forEach(uni => {
      console.log(`${uni.university}: Score ${uni.overall_score}, Rank ${uni.national_rank}`);
    });

    // Find universities with the exact same score
    const sameScoreUniversities = sortedUniversities.filter(
      uni => Math.abs(uni.overall_score - newScore) < 0.01
    );

    if (sameScoreUniversities.length > 0) {
      // Use the rank of universities with the same score
      const rank = sameScoreUniversities[0].national_rank;
      console.log('\nFound universities with same score:');
      sameScoreUniversities.forEach(uni => {
        console.log(`${uni.university}: Score ${uni.overall_score}, Rank ${uni.national_rank}`);
      });
      console.log('Using rank:', rank);
      return rank;
    }

    // If no exact matches, count universities with higher scores
    let higherScores = 0;
    for (const uni of sortedUniversities) {
      if (uni.overall_score > newScore) {
        higherScores++;
      } else {
        break;
      }
    }

    // Find the next lowest score's rank to interpolate
    const nextLowestUni = sortedUniversities.find(uni => uni.overall_score < newScore);
    const newRank = nextLowestUni ? nextLowestUni.national_rank - 1 : higherScores + 1;

    console.log(`\nFound ${higherScores} universities with higher scores`);
    console.log('Calculated new rank:', newRank);
    return newRank;
  } catch (error) {
    console.error("Error calculating new rank:", error);
    throw error;
  }
};